import { EMPTY_SLIP, readSlip, writeSlip, type DraftSlip } from './slip'

export type SlipStoreState = { slip: DraftSlip; isPlacing: boolean }
type SlipStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null

/**
 * Bozza della schedina condivisa da tutti i `useSlip` montati nella scheda, per torneo: una scrittura arriva subito
 * a ogni pagina (niente copia vecchia letta da `localStorage` mentre un'altra pagina sta giocando). `isPlacing` è la
 * POST in volo: finché c'è, nessuna pagina può rigiocare la stessa bozza. `localStorage` resta solo la persistenza.
 * Lo stato di un torneo è un oggetto nuovo a ogni cambio e lo stesso altrimenti (snapshot per `useSyncExternalStore`).
 */
export function createSlipStore(storage: () => SlipStorage) {
  const states = new Map<string, SlipStoreState>()
  const listeners = new Map<string, Set<() => void>>()

  const get = (tournamentId: string): SlipStoreState => {
    const state = states.get(tournamentId)
    if (state) return state
    const loaded = { slip: readSlip(storage(), tournamentId), isPlacing: false }
    states.set(tournamentId, loaded)
    return loaded
  }
  const update = (tournamentId: string, patch: Partial<SlipStoreState>) => {
    states.set(tournamentId, { ...get(tournamentId), ...patch })
    listeners.get(tournamentId)?.forEach((listener) => listener())
  }

  return {
    get,
    setSlip(tournamentId: string, slip: DraftSlip) {
      writeSlip(storage(), tournamentId, slip)
      update(tournamentId, { slip })
    },
    setPlacing: (tournamentId: string, isPlacing: boolean) => update(tournamentId, { isPlacing }),
    subscribe(tournamentId: string, listener: () => void) {
      const set = listeners.get(tournamentId) ?? new Set()
      listeners.set(tournamentId, set.add(listener))
      return () => void set.delete(listener)
    },
  }
}

export const EMPTY_SLIP_STATE: SlipStoreState = { slip: EMPTY_SLIP, isPlacing: false }
